import { isLocalStudyExperience } from '@/pages/try/studyExperience/mode';
import { api } from '@/lib/api';

/**
 * Study capture and AI page analysis (study-capture-analyse-prd.md §2, §4, §10.3). The server
 * owns the price, the charge and the result; the app only shows them. Analysis never contains
 * answers, and learner edits are free.
 */

export const STUDY_ANALYSIS_MAX_PAGES = 10;
export const STUDY_ANALYSIS_PAGE_MAX_BYTES = 2_000_000;
export const STUDY_ANALYSIS_PAGE_MAX_EDGE = 1600;
/** Fallback poll interval when the WebSocket is down (PRD §3.1). */
export const STUDY_ANALYSIS_POLL_MS = 3000;
export const STUDY_ANALYSIS_EVENT = 'study.analysis.updated';

export type StudyAnalysisStatus = 'queued' | 'running' | 'partial' | 'succeeded' | 'failed';
export type StudyAnalysisConfidence = 'high' | 'medium' | 'low';
export type StudyMaterialType =
  | 'homework'
  | 'class_notes'
  | 'textbook'
  | 'test_blank'
  | 'test_marked'
  | 'task_sheet'
  | 'slides'
  | 'other';

export const STUDY_MATERIAL_TYPES: ReadonlyArray<{ value: StudyMaterialType; label: string }> = [
  { value: 'homework', label: 'Homework' },
  { value: 'class_notes', label: 'Class notes' },
  { value: 'textbook', label: 'Textbook' },
  { value: 'test_blank', label: 'Test' },
  { value: 'test_marked', label: 'Marked test' },
  { value: 'task_sheet', label: 'Task sheet' },
  { value: 'slides', label: 'Slides' },
  { value: 'other', label: 'Other' },
];

export function materialTypeLabel(value: StudyMaterialType): string {
  return STUDY_MATERIAL_TYPES.find((type) => type.value === value)?.label ?? 'Other';
}

export interface StudyRegion {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface StudyPageRef {
  document_id: string;
  page_number: number;
}

export type StudyAnalysisBlockKind =
  | 'heading'
  | 'text'
  | 'math'
  | 'table'
  | 'diagram'
  | 'question'
  | 'answer_space'
  | 'teacher_mark';

export interface StudyAnalysisBlock {
  id: string;
  kind: StudyAnalysisBlockKind;
  text: string;
  region: StudyRegion | null;
  confidence: StudyAnalysisConfidence;
  provenance: 'ai_transcription' | 'learner_edit';
}

export interface StudyAnalysisPage {
  page_index: number;
  document_id: string;
  page_number: number;
  status: 'pending' | 'read' | 'failed';
  error_code: string | null;
  legible: 'good' | 'partial' | 'poor' | null;
  handwriting: 'none' | 'some' | 'mostly' | null;
  blocks: StudyAnalysisBlock[];
}

export interface StudyAnalysisQuestion {
  id: string;
  number: string;
  text: string;
  page_index: number;
  region: StudyRegion | null;
  kind: 'multiple_choice' | 'short_answer' | 'working' | 'extended' | 'other';
  marks: number | null;
  marked_wrong: boolean | null;
  source: 'ai' | 'learner';
  /** From the Study Sessions started for this question on the server (PRD §4.3). */
  status: StudyAnalysisQuestionStatus;
  /** The latest Study Session started for this question. */
  study_session_id: string | null;
  /** True when the text comes from the learner's correction of the read text. */
  text_edited: boolean;
}

/** `done` once any session for the question was finished; it stays done when started again. */
export type StudyAnalysisQuestionStatus = 'not_started' | 'in_progress' | 'done';

export type StudyAnalysisWarning =
  | 'poor_legibility'
  | 'mostly_handwriting'
  | 'mixed_subjects'
  | 'possible_assessment'
  | 'no_questions_found';

export interface StudyAnalysisResult {
  learning_note_id?: string;
  learning_notes?: {
    depth: 'summary' | 'in_depth' | 'complete';
    chapters: Array<{ title: string; body: string; block_ids: string[] }>;
  };
  material_type: StudyMaterialType;
  subject: { key: string | null; label: string; confidence: StudyAnalysisConfidence };
  topic: { label: string; confidence: StudyAnalysisConfidence };
  year_level_guess: { value: number | null; confidence: StudyAnalysisConfidence };
  questions: StudyAnalysisQuestion[];
  key_points: Array<{
    text: string;
    source: 'from_page' | 'ai_explanation';
    block_ids: string[];
  }>;
  key_terms: Array<{ term: string; meaning: string; source: 'from_page' | 'ai_explanation' }>;
  formulas: Array<{ latex: string; label: string | null; block_ids: string[] }>;
  dates: Array<{
    text: string;
    date: string | null;
    kind: 'due' | 'test' | 'other';
    confidence: StudyAnalysisConfidence;
  }>;
  warnings: StudyAnalysisWarning[];
  /**
   * Marking criteria copied from a task sheet (study-capture-fixes-prd.md G-04). Null for other
   * material types and for results saved before `study-analysis-v2`; absent on an older server.
   */
  rubric?: StudyRubric | null;
}

export interface StudyRubric {
  criteria: Array<{
    name: string;
    block_ids: string[];
    levels: Array<{ label: string; descriptor: string; block_ids: string[] }>;
  }>;
}

/**
 * A question found on a read page while the whole material is still being understood
 * (study-capture-fixes-prd.md F-09). It cannot be started; the final list replaces it.
 */
export interface StudyProvisionalQuestion {
  page_index: number;
  block_id: string;
  first_line: string;
  region: StudyRegion | null;
}

export interface StudyAnalysis {
  id: string;
  status: StudyAnalysisStatus;
  version: number;
  parent_analysis_id: string | null;
  primary_document_id: string;
  document_ids: string[];
  prompt_version: string;
  notes_depth?: StudyNotesDepth | null;
  delivery_stage?: 'queued' | 'reading' | 'notes' | 'knowledge' | 'ready' | 'failed';
  model_ids: string[];
  progress: {
    pages_total: number;
    pages_read: number;
    pages_failed: number;
    /** Questions shown now: AI questions minus removed ones, plus added ones. */
    questions_total: number;
    questions_done: number;
  };
  stars: { per_page: number; quoted: number; charged: number; refunded: number };
  /**
   * Refund of a failed analysis (PRD §15.3). `pending`: Stars are still owed and a settlement
   * job returns them later; `refunded`: Stars are back, on time or late (`REFUNDED_LATE`).
   */
  refund: StudyAnalysisRefund;
  /** Can be `REFUND_PENDING` (Stars still owed) or `REFUNDED_LATE` (owed Stars paid later). */
  error_code: string | null;
  created_at: string;
  completed_at: string | null;
  pages: StudyAnalysisPage[];
  result: StudyAnalysisResult | null;
  learner_confirmed: { subject: boolean; topic: boolean; material_type: boolean };
  edits: Array<{ id: string; kind: string; payload: unknown; created_at: string }>;
  /** Filled only while queued or running and no result is saved (F-09); absent on an older server. */
  provisional_questions?: StudyProvisionalQuestion[];
  /** When the learner first opened the finished result (F-10). */
  learner_seen_at?: string | null;
}

export type StudyAnalysisRefundState = 'refunded' | 'pending' | 'none';

export interface StudyAnalysisRefund {
  state: StudyAnalysisRefundState;
  owed_stars: number;
}

export interface StudyAnalysisQuote {
  pages: number;
  stars_per_page: number;
  stars_total: number;
  model: string;
  notes_depth?: StudyNotesDepth;
  includes?: ('study_notes' | 'learning_points' | 'explanations')[];
}

export type StudyAnalysisEdit =
  | { kind: 'subject'; subject_key: string | null; label: string }
  | { kind: 'topic'; label: string }
  | { kind: 'material_type'; material_type: StudyMaterialType }
  | { kind: 'block_text'; page_index: number; block_id: string; text: string }
  | {
      kind: 'question_add';
      page_index: number;
      number: string;
      text: string;
      region: StudyRegion | null;
    }
  | { kind: 'question_remove'; question_id: string };

export interface StudyDocumentPageUpload {
  document_id: string;
  page_number: number;
  mime_type: 'image/png';
  size_bytes: number;
  provenance: 'client_pdfjs' | 'client_photo';
}

/** A stored page PNG with a short-lived read URL. Analysis regions are fractions of it. */
export interface StudyDocumentPageImage extends StudyDocumentPageUpload {
  speaker_notes?: string | null;
  read_url: string;
  read_url_expires_in: number;
}

interface SignedPageUpload {
  url: string;
  method: 'PUT';
  headers: Record<string, string>;
  expires_in: number;
  s3_key: string;
  page_number: number;
  size_bytes: number;
}

export const STUDY_ANALYSIS_QUERY_KEY = (analysisId: string) =>
  ['study-analysis', analysisId] as const;
export const STUDY_DOCUMENT_ANALYSIS_QUERY_KEY = (documentId: string) =>
  ['study-document-analysis', documentId] as const;

export const FINISHED_ANALYSIS_STATUSES: ReadonlySet<StudyAnalysisStatus> = new Set([
  'partial',
  'succeeded',
  'failed',
]);

export function analysisInProgress(analysis: Pick<StudyAnalysis, 'status'> | undefined | null) {
  return analysis?.status === 'queued' || analysis?.status === 'running';
}

/** Uploads one prepared page PNG. The first image registered for a page wins on the server. */
export async function uploadStudyDocumentPage(
  documentId: string,
  pageNumber: number,
  image: Blob,
): Promise<StudyDocumentPageUpload> {
  if (isLocalStudyExperience())
    return api<StudyDocumentPageUpload>(
      `/study/experience/documents/${documentId}/pages/${pageNumber}`,
      { method: 'POST', body: { image } },
    );
  const input = { page_number: pageNumber, size_bytes: image.size };
  const signed = await api<SignedPageUpload>(`/study/documents/${documentId}/pages/upload-intent`, {
    method: 'POST',
    body: input,
  });
  // The signature may cover Content-Type, so a second spelling of the header must not be added.
  const hasType = Object.keys(signed.headers ?? {}).some(
    (name) => name.toLowerCase() === 'content-type',
  );
  const uploaded = await fetch(signed.url, {
    method: signed.method,
    headers: hasType ? signed.headers : { ...signed.headers, 'Content-Type': 'image/png' },
    body: image,
  });
  if (!uploaded.ok) throw new Error('A page did not upload. Check your connection and try again.');
  return api<StudyDocumentPageUpload>(`/study/documents/${documentId}/pages/complete`, {
    method: 'POST',
    body: { ...input, s3_key: signed.s3_key },
  });
}

export const getStudyDocumentPageImage = (documentId: string, pageNumber: number) =>
  api<StudyDocumentPageImage>(`/study/documents/${documentId}/pages/${pageNumber}`);

export type StudyNotesDepth = 'summary' | 'in_depth' | 'complete';
export const quoteStudyAnalysis = (pages: StudyPageRef[], notes_depth?: StudyNotesDepth) =>
  api<StudyAnalysisQuote>('/study/analyses/quote', {
    method: 'POST',
    body: { pages, ...(notes_depth ? { notes_depth } : {}) },
  });

export const createStudyAnalysis = (input: {
  pages: StudyPageRef[];
  expected_stars: number;
  idempotency_key: string;
  notes_depth?: StudyNotesDepth;
}) => api<StudyAnalysis>('/study/analyses', { method: 'POST', body: input });

export const getStudyAnalysis = (analysisId: string) =>
  api<StudyAnalysis>(`/study/analyses/${analysisId}`);

export const getStudyDocumentAnalysis = (documentId: string) =>
  api<StudyAnalysis>(`/study/documents/${documentId}/analysis`);

export const editStudyAnalysis = (analysisId: string, edit: StudyAnalysisEdit) =>
  api<StudyAnalysis>(`/study/analyses/${analysisId}/edits`, { method: 'POST', body: edit });

export const retryStudyAnalysis = (
  analysisId: string,
  input: { expected_stars: number; idempotency_key: string; notes_depth?: StudyNotesDepth },
) => api<StudyAnalysis>(`/study/analyses/${analysisId}/retry`, { method: 'POST', body: input });

/** Page references of an analysis, in page-index order. */
export function analysisPageRefs(analysis: Pick<StudyAnalysis, 'pages'>): StudyPageRef[] {
  return [...analysis.pages]
    .sort((a, b) => a.page_index - b.page_index)
    .map((page) => ({ document_id: page.document_id, page_number: page.page_number }));
}

/** Stars a retry costs: failed pages for a partial analysis, every page for a failed one. */
export function retryStars(analysis: Pick<StudyAnalysis, 'status' | 'progress' | 'stars'>) {
  const pages =
    analysis.status === 'partial' ? analysis.progress.pages_failed : analysis.progress.pages_total;
  return pages * analysis.stars.per_page;
}

/** The material analysis page. Without an analysis id it shows the newest analysis of the document. */
export function analysisPath(documentId: string, analysisId?: string | null) {
  const base = `/learn/study/materials/${encodeURIComponent(documentId)}/analysis`;
  return analysisId ? `${base}?analysis=${encodeURIComponent(analysisId)}` : base;
}

/** The Analyse button text, for example "Analyse 3 pages · 6★" (PRD §2, §6). */
export function analyseButtonLabel(pages: number, stars: number | null) {
  const count = `${pages} ${pages === 1 ? 'page' : 'pages'}`;
  return stars === null ? `Analyse ${count}` : `Analyse ${count} · ${stars}★`;
}

/** A short title for notices, such as "Linear equations homework". */
export function analysisTitle(analysis: Pick<StudyAnalysis, 'result'>, fallback: string): string {
  const result = analysis.result;
  if (!result?.topic.label) return fallback;
  return `${result.topic.label} ${materialTypeLabel(result.material_type).toLowerCase()}`;
}
